import { createFileRoute, Link } from "@tanstack/react-router";
import { AccountShell } from "@/components/customer/AccountShell";
import { useCustomerProfile } from "@/hooks/useCustomerAccount";
import { useAppointments } from "@/hooks/useAppointments";
import { formatISTDateTime } from "@/lib/date/ist";
import { Badge } from "@/components/ui/badge";
import { Loader2, Calendar, ChevronRight } from "lucide-react";

export const Route = createFileRoute("/_customer/account/appointments/")({
  component: CustomerAppointmentsPage,
});

function CustomerAppointmentsPage() {
  const { profile } = useCustomerProfile();
  const { appointments, isLoading } = useAppointments(profile?.id);

  const getStatusVariant = (status: string) => {
    switch (status) {
      case "confirmed": return "outline";
      case "pending": return "secondary";
      case "cancelled": return "destructive";
      default: return "outline";
    }
  };

  return (
    <AccountShell
      title="My Appointments"
      description="View and manage your health and nutrition consultations."
    >
      {isLoading ? (
        <div className="flex justify-center p-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : appointments.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border/60 p-12 text-center">
          <Calendar className="mx-auto mb-4 h-8 w-8 text-muted-foreground/40" />
          <h3 className="text-sm font-medium">No appointments found</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            You haven't booked any consultations yet.
          </p>
          <Link
            to="/appointments"
            className="mt-6 inline-flex rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground"
          >
            Book Appointment
          </Link>
        </div>
      ) : (
        <div className="grid gap-4">
          {appointments.map((apt) => (
            <Link
              key={apt.id}
              to="/account/appointments/$appointmentId"
              params={{ appointmentId: apt.id }}
              className="flex items-center justify-between rounded-xl border border-border/60 bg-card p-4 transition-colors hover:bg-secondary/30"
            >
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{(apt.consultation_types as any)?.name}</span>
                  <Badge variant={getStatusVariant(apt.status) as any} className="text-[10px] uppercase">
                    {apt.status}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground">
                  with {(apt.consultants as any)?.name}
                </div>
                <div className="mt-1 text-sm font-medium text-primary">
                  {formatISTDateTime(apt.start_time)}
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </Link>
          ))}
        </div>
      )}
    </AccountShell>
  );
}
